/**
 * Plan Invoice — form-level submit interceptors (Tier 3).
 *
 * Migrated from: plan/plan-invoice-base.coffee
 *   Plan_invoiceCardFormView.tab_do_save → warn_plan_onHold
 *
 * Checks plan status before allowing invoice save.
 */
import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FormValidatorFn } from "@neu/libui";

/**
 * WarnPlanOnHold — blocks invoice creation if the plan status is "Pending".
 * Old system checked snapshot_data().plan.status; new system fetches
 * the plan directly via API.
 */
export const WarnPlanOnHold: FormValidatorFn = async ({ values }) => {
	const planId = values.plan_id?.id;
	if (!planId) return { passed: true };

	try {
		const data = await fetchAPI(buildApiUrl(`/plan/${planId}`));
		const plan = Array.isArray(data) ? data[0] : data;
		if (plan?.status === "Pending") {
			return {
				passed: false,
				message: `For Plan ${plan.auto_name || plan.name || planId}, you cannot create Invoice as Plan Status is "Pending".`,
			};
		}
	} catch {
		// API error — don't block submission
	}

	return { passed: true };
};

function normalizeInvoiceRowMeta(
	rows: unknown[]
): Array<Record<string, unknown>> {
	if (!Array.isArray(rows)) return [];
	const deduped = new Map<number, Record<string, unknown>>();
	for (const row of rows) {
		if (!row || typeof row !== "object") continue;
		const rowObj = row as Record<string, unknown>;
		const metaRaw = rowObj.meta ?? rowObj._meta;
		const meta =
			typeof metaRaw === "number"
				? metaRaw
				: Number.parseInt(String(metaRaw ?? ""), 10);
		if (Number.isNaN(meta)) continue;
		deduped.set(meta, { ...rowObj, meta });
	}
	return [...deduped.values()];
}

export const normalizePlanInvoiceRows: FormValidatorFn = ({ values }) => {
	const normalizedDispense = normalizeInvoiceRowMeta(values.dispense ?? []);
	const normalizedAdjustment = normalizeInvoiceRowMeta(
		values.adjustment ?? []
	);
	return {
		passed: true,
		values: {
			dispense: normalizedDispense,
			adjustment: normalizedAdjustment,
		},
	};
};

export const populatePlanMedicalCarrierFields: FormValidatorFn = async ({
	values,
}) => {
	const carrierID = values.carrier_id?.id;
	if (!carrierID) return { passed: true };

	if (
		values.carrier_phone &&
		values.carrier_fax &&
		values.bin &&
		values.pcn
	) {
		return { passed: true };
	}

	try {
		const data = await fetchAPI(buildApiUrl(`/carrier/${carrierID}`));
		const carrier = Array.isArray(data) ? data[0] : data;
		if (!carrier) return { passed: true };
		return {
			passed: true,
			values: {
				carrier_phone: values.carrier_phone || carrier.phone || "",
				carrier_fax: values.carrier_fax || carrier.fax || "",
				bin: values.bin || carrier.bin || "",
				pcn: values.pcn || carrier.pcn || "",
			},
		};
	} catch {
		return { passed: true };
	}
};

export const populatePlanPBMFields: FormValidatorFn = async ({ values }) => {
	const pbmID = values.pbm_id?.id;
	if (!pbmID) return { passed: true };

	if (values.pbm_phone && values.pbm_fax && values.bin && values.pcn) {
		return { passed: true };
	}

	try {
		const data = await fetchAPI(buildApiUrl(`/pbm/${pbmID}`));
		const pbm = Array.isArray(data) ? data[0] : data;
		if (!pbm) return { passed: true };
		return {
			passed: true,
			values: {
				pbm_phone: values.pbm_phone || pbm.phone || "",
				pbm_fax: values.pbm_fax || pbm.fax || "",
				bin: values.bin || pbm.bin || "",
				pcn: values.pcn || pbm.pcn || "",
			},
		};
	} catch {
		return { passed: true };
	}
};
